import React, { useState, useEffect, useMemo } from 'react';
import { X, Send, MessageSquare, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import API_URL from '@/config/api';

/**
 * Reusable "Send message" modal. Renders inline as a fixed overlay.
 *
 * Props:
 *   open              : boolean
 *   onClose           : () => void
 *   channel           : 'sms' | 'whatsapp'
 *   recipientName     : string       — shown in the header
 *   recipientPhone    : string       — pre-filled, still editable
 *   recipientRef      : string?      — audit-trail id (installer user_id etc.)
 *   provider          : 'firetext' | 'superchat' (SMS provider — defaults 'firetext')
 *   broadcast         : {              — enables broadcast mode
 *     label: string,                   — e.g. "3 installers with ProBound"
 *     brand_access: string[],          — ['probound']
 *     installer_ids?: string[],
 *   } | null
 *   onSent            : () => void   — fired after successful send (e.g. to refresh history)
 */
export default function SendMessageModal({
  open,
  onClose,
  channel,
  recipientName,
  recipientPhone,
  recipientRef,
  provider = 'firetext',
  broadcast = null,
  onSent,
}) {
  const [to, setTo] = useState(recipientPhone || '');
  const [body, setBody] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [variables, setVariables] = useState([]);
  const [sending, setSending] = useState(false);

  // SMS text templates — a separate catalogue from WhatsApp Meta templates.
  // Fetched on open when the channel is SMS. Structure: [{id, name, body,
  // category, placeholders: ['first_name', ...]}].
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [smsTemplateId, setSmsTemplateId] = useState('');
  const [smsVars, setSmsVars] = useState({}); // { first_name: 'Simon', ... }

  useEffect(() => {
    if (open) {
      setTo(recipientPhone || '');
      setBody('');
      setTemplateId('');
      setVariables([]);
      setSmsTemplateId('');
      setSmsVars({});
    }
  }, [open, recipientPhone]);

  // Fetch WhatsApp templates when opening the WhatsApp modal.
  useEffect(() => {
    if (!open || channel !== 'whatsapp') return;
    let cancelled = false;
    (async () => {
      setLoadingTemplates(true);
      try {
        const res = await fetch(`${API_URL}/api/messages/whatsapp-templates`, { credentials: 'include' });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (!cancelled) setTemplates(data.templates || []);
      } catch (e) {
        if (!cancelled) setTemplates([]);
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, channel]);

  // Fetch SMS text templates when opening the SMS modal. Silent-fail —
  // dropdown just won't render if the endpoint 403s (non-staff) or fails.
  useEffect(() => {
    if (!open || channel !== 'sms') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/messages/sms-templates`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const list = await res.json();
        if (!cancelled) setSmsTemplates(Array.isArray(list) ? list : []);
      } catch (_e) {
        if (!cancelled) setSmsTemplates([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, channel]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === templateId),
    [templates, templateId],
  );

  const selectedSmsTemplate = useMemo(
    () => smsTemplates.find((t) => t.id === smsTemplateId) || null,
    [smsTemplates, smsTemplateId],
  );

  // When a template is picked or a variable value changes, re-render the
  // body live so the composer preview + send payload always match.
  useEffect(() => {
    if (channel !== 'sms' || !selectedSmsTemplate) return;
    const rendered = (selectedSmsTemplate.body || '').replace(
      /{{\s*([a-zA-Z0-9_]+)\s*}}/g,
      (_m, k) => String(smsVars[k] || ''),
    );
    setBody(rendered);
  }, [channel, selectedSmsTemplate, smsVars]);

  // Keep the variables array sized to the template's placeholder count.
  useEffect(() => {
    if (!selectedTemplate) return;
    const n = selectedTemplate.variable_count || 0;
    setVariables((prev) => Array.from({ length: n }, (_, i) => prev[i] || ''));
  }, [selectedTemplate]);

  if (!open) return null;

  const channelLabel = channel === 'whatsapp' ? 'WhatsApp' : 'SMS Text';
  const providerLabel = channel === 'whatsapp' ? 'Superchat' : (provider === 'superchat' ? 'Superchat' : 'Firetext');
  const isBroadcast = Boolean(broadcast);

  const canSend = isBroadcast
    ? (channel === 'whatsapp' ? Boolean(templateId) : body.trim().length > 0)
    : (to.trim().length >= 7 && (channel === 'whatsapp' ? Boolean(templateId) : body.trim().length > 0));

  const handleSend = async () => {
    if (!canSend || sending) return;
    setSending(true);
    try {
      let url;
      let payload;
      if (isBroadcast) {
        url = `${API_URL}/api/messages/broadcast`;
        payload = {
          channel: channel === 'whatsapp' ? 'superchat_whatsapp' : (provider === 'superchat' ? 'superchat_sms' : 'firetext_sms'),
          body: channel === 'whatsapp' ? '' : body,
          template_id: channel === 'whatsapp' ? templateId : null,
          variables: channel === 'whatsapp' ? variables : [],
          brand_access: broadcast.brand_access || [],
          installer_ids: broadcast.installer_ids || [],
        };
      } else {
        url = `${API_URL}/api/messages/send`;
        payload = channel === 'whatsapp'
          ? { channel: 'superchat_whatsapp', to, body: '', template_id: templateId, variables, recipient_ref: recipientRef }
          : { channel: provider === 'superchat' ? 'superchat_sms' : 'firetext_sms', to, body, recipient_ref: recipientRef };
      }
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Send failed');
      if (isBroadcast) {
        const parts = [`${data.sent_count} sent`];
        if (data.failed_count) parts.push(`${data.failed_count} failed`);
        if (data.skipped_no_phone_count) parts.push(`${data.skipped_no_phone_count} skipped (no phone)`);
        toast.success(`Broadcast: ${parts.join(' · ')}`);
      } else if (data.status === 'test_ok') {
        toast.success(`${channelLabel} queued (TEST MODE — no ${providerLabel} key set yet)`);
      } else {
        toast.success(`${channelLabel} queued via ${providerLabel} · id ${data.provider_id || 'pending'}`);
      }
      if (onSent) onSent();
      onClose();
    } catch (e) {
      toast.error(`Send failed: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" data-testid={`send-${channel}-modal`}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between p-5 border-b">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <MessageSquare className={`w-5 h-5 ${channel === 'whatsapp' ? 'text-emerald-600' : 'text-blue-600'}`} />
              {isBroadcast ? `Broadcast ${channelLabel}` : `${channelLabel} to ${recipientName || 'contact'}`}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              via {providerLabel}
              {isBroadcast && broadcast?.label ? ` · ${broadcast.label}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="send-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!isBroadcast && (
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Mobile number</label>
              <Input
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="07…"
                className="mt-1"
                data-testid="send-modal-phone"
              />
            </div>
          )}

          {channel === 'whatsapp' ? (
            <>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Template</label>
                {loadingTemplates ? (
                  <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading Superchat templates…
                  </div>
                ) : templates.length === 0 ? (
                  <div className="mt-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    No Superchat templates available yet. Create + submit them in Superchat under
                    <b> Automations → WhatsApp Templates</b>, wait for Meta approval, then hit refresh.
                  </div>
                ) : (
                  <select
                    value={templateId}
                    onChange={(e) => setTemplateId(e.target.value)}
                    className="mt-1 w-full text-sm border rounded-md px-3 py-2 bg-white focus:outline-none focus:border-emerald-500"
                    data-testid="send-modal-template"
                  >
                    <option value="">— pick a template —</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id} disabled={t.status && t.status.toLowerCase() !== 'approved'}>
                        {t.name || t.id} {t.status && t.status.toLowerCase() !== 'approved' ? `(${t.status})` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {selectedTemplate && (
                <>
                  {selectedTemplate.body && (
                    <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-900 whitespace-pre-wrap">
                      {selectedTemplate.body}
                    </div>
                  )}
                  {variables.map((v, i) => (
                    <div key={i}>
                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Variable {`{{${i + 1}}}`}
                      </label>
                      <Input
                        value={v}
                        onChange={(e) => setVariables((prev) => prev.map((x, idx) => idx === i ? e.target.value : x))}
                        className="mt-1"
                        data-testid={`send-modal-var-${i}`}
                      />
                    </div>
                  ))}
                </>
              )}
            </>
          ) : (
            <div>
              {/* SMS template picker — dropdown appears only when the staff
                  has at least one active template. Selecting a template
                  fills the body; per-placeholder inputs render below. */}
              {smsTemplates.length > 0 && (
                <div className="mb-3">
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Use a template</label>
                  <select
                    value={smsTemplateId}
                    onChange={(e) => { setSmsTemplateId(e.target.value); setSmsVars({}); }}
                    className="mt-1 w-full text-sm border rounded-md px-3 py-2 bg-white focus:outline-none focus:border-blue-500"
                    data-testid="send-modal-sms-template"
                  >
                    <option value="">— pick a template (optional) —</option>
                    {smsTemplates.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.category ? `[${t.category}] ` : ''}{t.name}
                      </option>
                    ))}
                  </select>
                  {selectedSmsTemplate && selectedSmsTemplate.placeholders?.length > 0 && (
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="send-modal-sms-template-vars">
                      {selectedSmsTemplate.placeholders.map((p) => (
                        <div key={p}>
                          <label className="text-[10px] font-mono text-gray-500">{`{{${p}}}`}</label>
                          <Input
                            value={smsVars[p] || ''}
                            onChange={(e) => setSmsVars((v) => ({ ...v, [p]: e.target.value }))}
                            placeholder={p.replace(/_/g, ' ')}
                            className="mt-0.5"
                            data-testid={`send-modal-sms-var-${p}`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Message</label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={5}
                maxLength={612}
                placeholder="Type your message…"
                className="mt-1"
                data-testid="send-modal-body"
              />
              <p className="text-[11px] text-gray-500 mt-1 text-right">
                {body.length}/612 · SMS is charged per 160-char segment
              </p>
            </div>
          )}
        </div>

        <div className="p-4 border-t flex items-center justify-end gap-2 bg-gray-50 rounded-b-2xl">
          <Button variant="outline" size="sm" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button
            size="sm"
            onClick={handleSend}
            disabled={!canSend || sending}
            className={channel === 'whatsapp' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'}
            data-testid="send-modal-submit"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
            {sending ? 'Sending…' : `Send ${channelLabel}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
